import React from 'react';
import { connect } from 'react-redux';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../local-types';
// Module
import { selectors as errorSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import {
    actions as companyActions,
    selectors as companySelectors,
    types as companyTypes,
} from '../../../redux/reducers/companies';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
// Ressources
import styles from './company-add-style';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { Industry } from '../../../redux/reducers/api-types';

interface CompanyAddProps extends WithTranslation, WithStyles, RouteComponentProps {
    industries: Industry[];
    saveCompany: (payload) => any;
    getCompanyIndustries: () => any;
    getIndustriesError: ErrorPayload;
    getSaveCompaniesError: ErrorPayload;
    isSavingCompany: boolean;
}

interface CompanyAddPageState {
    formData: any;
}

class CompanyAddPage extends React.Component<CompanyAddProps, CompanyAddPageState> {
    inputs: FormDatas[];

    constructor(props: CompanyAddProps) {
        super(props);
        this.state = {
            formData: {
                name: '',
                billing_address: { street: '', postal_code: '', city: '', country: '' },
                industry: { id: '' },
            },
        };
    }

    componentDidMount() {
        const industries = this.props.industries;
        if (!industries?.length) {
            this.props.getCompanyIndustries();
        }
    }

    inputsBuilder = (): FormDatas[] => {
        const { t, industries } = this.props;

        const industryOptions: FormPickerDatas[] = industries;

        return [
            {
                title: t('add.title'),
                datas: [
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    { id: 'siren', label: t('siren') },
                    { id: 'vat_number', label: t('vat_number') },
                    { id: 'purchase_order', label: t('purchase_order') },
                    {
                        id: 'industry.id',
                        label: t('industry'),
                        noEmpty: true,
                        required: true,
                        options: industryOptions,
                    },
                ],
            },
            {
                title: t('add.address'),
                datas: [
                    {
                        id: 'billing_address[street]',
                        label: t('common:form.address.street'),
                        required: true,
                    },
                    {
                        id: 'billing_address[postal_code]',
                        label: t('common:form.address.postal_code'),
                        required: true,
                    },
                    {
                        id: 'billing_address[city]',
                        label: t('common:form.address.city'),
                        required: true,
                    },
                    {
                        id: 'billing_address[country]',
                        label: t('common:form.address.country'),
                        required: true,
                    },
                ],
            },
        ];
    };

    onSubmit = (formData) => {
        const { saveCompany } = this.props;
        saveCompany(formData);
    };

    render() {
        const { getSaveCompaniesError, getIndustriesError, isSavingCompany } = this.props;
        const formPayload = this.state.formData;

        return (
            <App needRights={{ name: AccountRight.COMPANY_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={getSaveCompaniesError || getIndustriesError} />
                <EnhancedForm
                    payload={formPayload}
                    inputs={this.inputsBuilder()}
                    backRoute={paths.companies}
                    onSubmit={this.onSubmit}
                    loader={isSavingCompany}
                />
            </App>
        );
    }
}

const CompanyAddPageWithStyleAndProps = withStyles(styles)(withTranslation(['company', 'common'])(CompanyAddPage));

const mapStateToProps = (state) => ({
    industries: companySelectors.getIndustries(state),
    getIndustriesError: errorSelectors.getIndustriesError(state),
    getSaveCompaniesError: errorSelectors.getSaveCompaniesError(state),
    isSavingCompany: pendingSelectors.isPending(state, companyTypes.SAVE_COMPANY),
});

const mapDispatchToProps = {
    saveCompany: companyActions.saveCompany,
    getCompanyIndustries: companyActions.getCompanyIndustries,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAddPageWithStyleAndProps);
